import { useCallback } from 'react';

import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import type { MostPermissiveUserStorableRole, StorableWithRoleInfo } from 'src/helpers';
import { getMostPermissiveUserStorableRole } from 'src/helpers';

type GetStorableReturn =
  | NonNullable<ReturnType<typeof Apollo.useStorableMetadataQuery>['data']>['storableMetadata']
  | null;

export function useGetStorable(storableId: UUID | null): {
  loading: boolean;
  storable: GetStorableReturn;
} {
  const { data, loading } = Apollo.useStorableMetadataQuery({
    skip: !storableId,
    variables: { id: storableId ?? '' },
  });
  if (!data) return { loading, storable: null };

  return { loading, storable: data.storableMetadata };
}

export function useGetStorableLazy(storableId: UUID): {
  getStorable: () => ReturnType<
    NonNullable<ReturnType<typeof Apollo.useStorableMetadataLazyQuery>[0]>
  >;
  loading: boolean;
  storable:
    | NonNullable<Apollo.StorableMetadataLazyQueryHookResult[1]['data']>['storableMetadata']
    | null;
} {
  const [getStorableQuery, { data, loading }] = Apollo.useStorableMetadataLazyQuery();
  const getStorable = useCallback(
    () => getStorableQuery({ variables: { id: storableId } }),
    [getStorableQuery, storableId]
  );
  if (!data) return { getStorable, loading, storable: null };

  return { getStorable, loading, storable: data.storableMetadata };
}

interface UseMostPermissiveUserStorableRole extends MostPermissiveUserStorableRole {
  loading: boolean;
  storable: ReturnType<typeof useGetStorable>['storable'];
}

/**
 * Determines the most permissive PermissionRole for the current user.
 */
export function useMostPermissiveUserStorableRole(
  storableId: UUID | null
): UseMostPermissiveUserStorableRole {
  const currentUserId = useCurrentUserId();
  const { loading, storable } = useGetStorable(storableId);

  return { ...getMostPermissiveUserStorableRole(currentUserId, storable), loading, storable };
}

interface UseIsAuthorizedToEdit {
  canEdit: boolean;
  storable: ReturnType<typeof useGetStorable>['storable'];
}

/**
 * Determines whether the current user is authorized to edit the Storable.
 */
export function useIsAuthorizedToEditStorable(storableId: UUID | null): UseIsAuthorizedToEdit {
  const { isEditor, isOwner, storable } = useMostPermissiveUserStorableRole(storableId);

  return { canEdit: isOwner || isEditor, storable };
}

export function useGetIsAuthorizedToEditStorable() {
  const currentUserId = useCurrentUserId();
  const safeGetIsAuthorizedToEditStorabe = useCallback(
    (storable: StorableWithRoleInfo) => {
      const res = getMostPermissiveUserStorableRole(currentUserId, storable);
      return { ...res, canEdit: res.isOwner || res.isEditor };
    },
    [currentUserId]
  );

  return { getIsAuthorizedToEditStorable: safeGetIsAuthorizedToEditStorabe };
}
